import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, TextField } from '@mui/material';
import { useUuid, useCustomSnackbar } from '@procureai/ui-mui';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { SvbrgFormStepType, IntakeRequestDescriptionQuery } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatActionNext } from '~/components/ChatStep/ChatActions/ChatActionNext';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { useFormFieldErrorTranslation } from '~/hooks';
import { useChatSession } from '~/modules';
import {
  useExtractedRequirements,
  shouldPrePopulateFromRequirements,
} from '~/routes/IntakeDetail/utils/useExtractedRequirements';

import { INTAKE_REQUEST_DESCRIPTION_STEP_MUTATION } from './IntakeRequestDescriptionStep.mutation.gql';
import { INTAKE_REQUEST_DESCRIPTION } from './IntakeRequestDescriptionStep.query.gql';
import {
  IntakeRequestDescriptionStepFormData,
  intakeRequestDescriptionStepFormDataDefault,
  intakeRequestDescriptionStepFormDataSchema,
} from './IntakeRequestDescriptionStep.schema';

const IntakeRequestDescriptionStepForm = ({
  data,
  formId,
  disabled,
}: {
  data: IntakeRequestDescriptionQuery;
  formId: string;
  disabled?: boolean;
}) => {
  const { t, i18n } = useTranslation(I18nNamespace.intake);
  const { onError } = useCustomSnackbar();
  const session = useChatSession();

  // Get extracted requirements for this step
  const { hasData: hasExtractedData, data: extractedData } = useExtractedRequirements({
    chatSessionId: session?.id || '',
    stepType: SvbrgFormStepType.REQUEST_DESCRIPTION,
    enabled: Boolean(session?.id),
  });

  const form = useForm<IntakeRequestDescriptionStepFormData>({
    mode: 'onTouched',
    resolver: zodResolver(intakeRequestDescriptionStepFormDataSchema),
    defaultValues: intakeRequestDescriptionStepFormDataDefault,
  });

  useEffect(() => {
    const requestDescription = data?.sevenbridges.chatSession.requestDescription;

    // Priority 1: Use existing request description if available
    if (requestDescription) {
      form.reset(requestDescription);
      return;
    }

    // Priority 2: Pre-populate from extracted requirements if available and no existing data
    if (shouldPrePopulateFromRequirements(hasExtractedData, requestDescription)) {
      const prePopulatedData = {
        ...intakeRequestDescriptionStepFormDataDefault,
        text: extractedData?.text || intakeRequestDescriptionStepFormDataDefault.text,
        language: extractedData?.language || intakeRequestDescriptionStepFormDataDefault.language,
      };
      form.reset(prePopulatedData);
    }
  }, [form, data, hasExtractedData, extractedData, i18n.language]);

  const [save] = useMutation(INTAKE_REQUEST_DESCRIPTION_STEP_MUTATION, {
    onError,
  });

  const { errorToHelperText } = useFormFieldErrorTranslation();
  return (
    <FormProvider {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(
          (data) => {
            save({
              variables: { data: { ...data, chatSessionId: session.id } },
            });
          },
          (error) => console.log(error),
        )}
        autoComplete="off"
        noValidate
      >
        <Controller
          name="text"
          render={({ field, fieldState: { error } }) => {
            return (
              <TextField
                {...field}
                variant="standard"
                label={t('IntakeRequestDescriptionStepForm.text.label')}
                multiline
                error={!!error}
                helperText={errorToHelperText(error)}
                fullWidth
                disabled={disabled || field.disabled}
              />
            );
          }}
        />
      </form>
    </FormProvider>
  );
};

export const IntakeRequestDescriptionStep = ({ role, createdBy, chatSessionCompleted }: ChatMessageProps) => {
  const { t: tGlobal } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestDescriptionStep' });
  const session = useChatSession();
  const formId = useUuid();

  const { data } = useQuery(INTAKE_REQUEST_DESCRIPTION, {
    variables: session,
  });

  return (
    <ChatStep
      title={tGlobal('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={<ChatActionNext formId={formId} disabled={chatSessionCompleted} />}
    >
      <Box
        sx={{
          paddingTop: 2,
        }}
      >
        {data && <IntakeRequestDescriptionStepForm data={data} formId={formId} disabled={chatSessionCompleted} />}
      </Box>
    </ChatStep>
  );
};
